import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';

interface StaffMember {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  jobTitle?: string;
  department?: string;
  activeStatus?: number | boolean;
  hasLoginAccess?: number | boolean;
  nrcNumber: string;
  baseSalary?: number;
  housingAllowance?: number;
  transportAllowance?: number;
  responsibilityAllowance?: number;
  otherAllowances?: number;
}

interface Period {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  isLocked: boolean;
  status: 'open' | 'closed' | 'archived';
}

interface PayrollRecord {
  id: number;
  teacherId: number;
  periodId: number;
  baseSalary: number;
  housingAllowance: number;
  transportAllowance: number;
  responsibilityAllowance: number;
  otherAllowances: number;
  grossSalary: number;
  totalDeductions: number;
  netSalary: number;
  napsaDeduction: number;
  payeDeduction: number;
  nhimaDeduction: number;
  personalLevyDeduction?: number;
  otherDeductions?: number;
  status: string;
  paymentDate: string | null;
  createdAt: string;
  staffMember?: StaffMember;
  teacher?: StaffMember;
  period?: Period;
}

export const usePayrollData = () => {
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [payrolls, setPayrolls] = useState<PayrollRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination & Filtering State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [staffCategory, setStaffCategory] = useState('all');
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || '';
      const headers = { Authorization: `Bearer ${token}` };
      const errors: string[] = [];

      const formatError = (label: string, err: any) => {
        const status = err.response?.status;
        const message = err.response?.data?.message || err.message || 'Unknown error';
        return `Failed to load ${label}${status ? ` (${status})` : ''}: ${message}`;
      };

      // Fetch staff payroll profiles
      try {
        const staffRes = await axios.get(getApiUrl('payroll/teachers'), { headers });
        const data = Array.isArray(staffRes.data)
          ? staffRes.data
          : staffRes.data.teachers || staffRes.data.users || staffRes.data.data || [];
        let mergedStaff = data;

        const hasHeadteachers = data.some((member: StaffMember) => (member.role || '').toLowerCase() === 'headteacher');
        if (!hasHeadteachers) {
          try {
            const usersRes = await axios.get(getApiUrl('users/all'), { headers });
            const users = Array.isArray(usersRes.data)
              ? usersRes.data
              : usersRes.data.users || usersRes.data.data || [];
            const headteachers = users.filter((user: StaffMember) => (user.role || '').toLowerCase() === 'headteacher');
            if (headteachers.length > 0) {
              const existingIds = new Set(mergedStaff.map((member: StaffMember) => member.id));
              mergedStaff = [...mergedStaff, ...headteachers.filter((headteacher: StaffMember) => !existingIds.has(headteacher.id))];
            }
          } catch (err: any) {
            errors.push(formatError('headteachers', err));
          }
        }

        setStaffMembers(mergedStaff);
      } catch (err: any) {
        errors.push(formatError('staff', err));
      }

      // Fetch payroll periods
      try {
        const periodsRes = await axios.get(getApiUrl('payroll/periods'), { headers });
        const periodData = Array.isArray(periodsRes.data) ? periodsRes.data : [];
        const monthIndex: Record<string, number> = {
          jan: 0, january: 0,
          feb: 1, february: 1,
          mar: 2, march: 2,
          apr: 3, april: 3,
          may: 4,
          jun: 5, june: 5,
          jul: 6, july: 6,
          aug: 7, august: 7,
          sep: 8, sept: 8, september: 8,
          oct: 9, october: 9,
          nov: 10, november: 10,
          dec: 11, december: 11
        };
        const parsePeriodName = (name: string) => {
          const parts = name.trim().split(/\s+/);
          if (parts.length < 2) return 0;
          const monthKey = parts[0].toLowerCase();
          const year = parseInt(parts[parts.length - 1], 10);
          if (!Number.isFinite(year)) return 0;
          const month = monthIndex[monthKey];
          if (month === undefined) return 0;
          return new Date(year, month, 1).getTime();
        };
        const toTime = (value: string, name?: string) => {
          const ts = Date.parse(value);
          if (!Number.isNaN(ts)) return ts;
          return name ? parsePeriodName(name) : 0;
        };
        const sortedPeriods = [...periodData].sort((a, b) => {
          const timeDiff = toTime(b.startDate, b.name) - toTime(a.startDate, a.name);
          if (timeDiff !== 0) return timeDiff;
          return (b.id || 0) - (a.id || 0);
        });
        setPeriods(sortedPeriods);
        if (sortedPeriods.length > 0 && selectedPeriod === 'all') {
          setSelectedPeriod(String(sortedPeriods[0].id));
          setPage(1);
        }
      } catch (err: any) {
        errors.push(formatError('payroll periods', err));
      }

      // Fetch payroll records (with filters)
      try {
        const params: any = { page, limit };
        if (search) params.search = search;
        if (selectedPeriod !== 'all') params.periodId = selectedPeriod;
        if (selectedStatus !== 'all') params.status = selectedStatus;
        if (staffCategory !== 'all') params.staffCategory = staffCategory;

        const payrollsRes = await axios.get(getApiUrl('payroll'), {
          headers,
          params
        });

        const rawData = payrollsRes.data?.data || [];
        const meta = payrollsRes.data?.meta || { total: 0, totalPages: 0 };

        // Map data
        const mappedPayrolls = rawData.map((p: any) => ({
          ...p,
          staffMember: p.teacher || p.staffMember || {
            id: p.teacherId,
            name: p.teacherName || 'Unknown',
            email: p.teacherEmail || '',
            nrcNumber: p.nrcNumber || '',
            role: p.teacherRole || p.role || p.employeeRole || '',
            jobTitle: p.jobTitle || '',
            department: p.department || '',
            activeStatus: p.activeStatus,
            hasLoginAccess: p.hasLoginAccess
          },
          teacher: p.teacher || p.staffMember || {
            id: p.teacherId,
            name: p.teacherName || 'Unknown',
            email: p.teacherEmail || '',
            nrcNumber: p.nrcNumber || '',
            role: p.teacherRole || p.role || p.employeeRole || '',
            jobTitle: p.jobTitle || '',
            department: p.department || '',
            activeStatus: p.activeStatus,
            hasLoginAccess: p.hasLoginAccess
          },
          period: p.period || {
            id: p.periodId,
            name: p.periodName || 'Unknown Period',
            startDate: p.startDate || '',
            endDate: p.endDate || ''
          }
        }));

        setPayrolls(mappedPayrolls);
        setTotalPages(meta.totalPages);
        setTotalRecords(meta.total);
      } catch (err: any) {
        errors.push(formatError('payroll records', err));
      }

      if (errors.length > 0) {
        setError(errors.join(' | '));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, selectedPeriod, selectedStatus, staffCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle search with a small delay or manual trigger? 
  // Let's keep it simple and just re-fetch if fetchData is called manually for search

  return {
    teachers: staffMembers,
    staffMembers,
    periods,
    payrolls,
    loading,
    error,
    success,
    page,
    limit,
    search,
    selectedPeriod,
    selectedStatus,
    staffCategory,
    totalPages,
    totalRecords,
    setPage,
    setLimit,
    setSearch,
    setSelectedPeriod,
    setSelectedStatus,
    setStaffCategory,
    setError,
    setSuccess,
    fetchData
  };
};
